import React, { useCallback, useEffect, useState } from 'react';
import {
  approveLscMemberClaim,
  listLscMemberClaims,
  rejectLscMemberClaim,
  LscApiError,
} from '../api';
import type { LscMemberClaim } from '../types';
import ErrorBanner from './ErrorBanner';

interface MemberClaimQueuePanelProps {
  leagueId: string;
  onDecision?: () => void;
}

function confidenceBadgeClass(confidence?: string): string {
  if (confidence === 'high') return 'bg-emerald-900/50 text-emerald-200';
  if (confidence === 'medium') return 'bg-amber-900/50 text-amber-200';
  return 'bg-secondary-800 text-secondary-300';
}

const MemberClaimQueuePanel: React.FC<MemberClaimQueuePanelProps> = ({
  leagueId,
  onDecision,
}) => {
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [claims, setClaims] = useState<LscMemberClaim[]>([]);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLscMemberClaims(leagueId);
      setClaims(data.claims || []);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load claim queue.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadClaims();
  }, [loadClaims]);

  const handleApprove = async (claimId: string) => {
    setActingId(claimId);
    setError(null);
    try {
      await approveLscMemberClaim(leagueId, claimId);
      await loadClaims();
      onDecision?.();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not approve claim.');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (claimId: string) => {
    setActingId(claimId);
    setError(null);
    try {
      await rejectLscMemberClaim(leagueId, claimId, 'Rejected by commissioner');
      await loadClaims();
      onDecision?.();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not reject claim.');
    } finally {
      setActingId(null);
    }
  };

  const pendingClaims = claims.filter((claim) => claim.status === 'pending');

  if (loading) {
    return (
      <div className="card" data-testid="member-claim-queue-panel">
        <p className="text-sm text-secondary-400">Loading pending claims...</p>
      </div>
    );
  }

  if (pendingClaims.length === 0) {
    return null;
  }

  return (
    <div className="card space-y-4" data-testid="member-claim-queue-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Pending claims</h2>
        <p className="text-sm text-secondary-400">
          Review member requests to link imported NeonSportz roster slots.
        </p>
      </div>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left text-secondary-300">
          <thead>
            <tr className="text-secondary-400 border-b border-secondary-700">
              <th className="py-2 pr-4">Claimant</th>
              <th className="py-2 pr-4">Claimant Discord</th>
              <th className="py-2 pr-4">Team</th>
              <th className="py-2 pr-4">Imported Discord</th>
              <th className="py-2 pr-4">In-game</th>
              <th className="py-2 pr-4">Confidence</th>
              <th className="py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingClaims.map((claim) => {
              const claimId = claim.id || claim.claim_id || '';
              return (
                <tr key={claimId} className="border-b border-secondary-800" data-testid="claim-queue-row">
                  <td className="py-2 pr-4">
                    {claim.claimant_twitch_login
                      ? `@${claim.claimant_twitch_login}`
                      : claim.claimant_uid}
                  </td>
                  <td className="py-2 pr-4">
                    {claim.claimant_discord_username || claim.claimant_discord_id || '—'}
                  </td>
                  <td className="py-2 pr-4">{claim.imported_member_side_label || '—'}</td>
                  <td className="py-2 pr-4">
                    {claim.imported_member_discord_username || claim.imported_member_discord_id || '—'}
                  </td>
                  <td className="py-2 pr-4">{claim.imported_member_in_game_name || '—'}</td>
                  <td className="py-2 pr-4">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs uppercase ${confidenceBadgeClass(claim.confidence)}`}
                    >
                      {claim.confidence || 'unknown'}
                    </span>
                  </td>
                  <td className="py-2 space-x-2">
                    <button
                      type="button"
                      className="btn-primary text-xs"
                      disabled={actingId === claimId}
                      onClick={() => void handleApprove(claimId)}
                      data-testid="approve-claim-button"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      disabled={actingId === claimId}
                      onClick={() => void handleReject(claimId)}
                      data-testid="reject-claim-button"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemberClaimQueuePanel;
